import { useMemo, useCallback } from 'react'
import { useSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'
import {
  getFiltersByCollection,
  getSearchStringByCollection,
} from 'selectors/search'
import { isString } from 'utils/types'
import {
  setSearchString,
  setFilters,
  handleSubmitSearch,
  resetAllSearch,
} from 'actions/search'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { resetCollections } from 'actions/collection'
import {
  FEATURES,
  MODULE_TYPE,
  XMC4,
  OUPUT_PIN_SCHEMA,
  SCHEMA,
  XMC3,
  INPUT_PIN_SCHEMA,
  FAST_FEATURES_EXCLUDED_SUBTYPES,
  ADD_REMOVE_FEATURES,
  ADD_REMOVE_FEATURE_COLLECTIONS,
  APPLY_FAST_FEATURES,
  FEATURE,
  FEATURE_COLLECTIONS,
  FAST_FEATURES,
} from 'utils/constants/vehicle-constants'
import { propEq } from 'ramda'
import { selectVehicleBySerial } from 'selectors/truck'

// Constant defining the EXCLUSION RULES (Module + Schema combinations)
const EXCLUSION_RULES = [
  { moduleType: XMC3, schema: INPUT_PIN_SCHEMA },
  { moduleType: XMC4, schema: INPUT_PIN_SCHEMA },
  { moduleType: XMC4, schema: OUPUT_PIN_SCHEMA },
]

/**
 * @function checkFilterForExclusion
 * Helper logic to check if an active filter corresponds to the exclusion rules
 * @param {Object} filterObject - The current active filter map.
 * @returns {boolean} true if the filter matches an exclusion rule.
 */
const checkFilterForExclusion = (filterObject) => {
  if (!filterObject) return false

  const entries = Object.entries(filterObject)

  const result = EXCLUSION_RULES.some((rule) => {
    let moduleTypeForFilter = null
    let schemaForFilter = null
    // Use reduce to find if any combination of Module Type and Pin Schema matches an exclusion rule.
    const isExcluded = entries.reduce(
      (isMatch, [key, value]) => {
        // Exit early if an exclusion has already been found.
        if (isMatch || isString(value)) return isMatch

        if (!moduleTypeForFilter)
          moduleTypeForFilter = propEq(MODULE_TYPE, rule.moduleType, value)
        if (!schemaForFilter)
          schemaForFilter = propEq(SCHEMA, rule.schema, value)

        if (moduleTypeForFilter && schemaForFilter) return true

        return isMatch
      },
      false // Initial value: not excluded
    )
    return isExcluded
  })

  return result
}

/**
 * @function checkFilterForSmartSwitch
 * Helper logic to check if a Smart Switch filter is active (excluding Custom Switches)
 * @param {Object} filtersObject - The filters object from Redux state.
 * @returns {boolean} true if a Smart Switch is in filters (but NOT custom_switch).
 */
const checkFilterForSmartSwitch = (filtersObject) => {
  if (!filtersObject) return false

  // Check if any filter has filterType === "switches"
  // BUT exclude custom switches (custom_switch, Custom Switch, or custom in switches value)
  return Object.values(filtersObject).some((filter) => {
    if (filter?.filterType !== 'switches') return false

    // Check if this is a Custom Switch - if so, don't hide the toggle
    const switchValue = filter?.switches || ''
    const isCustomSwitch =
      switchValue === 'custom_switch' ||
      switchValue === 'Custom Switch' ||
      switchValue.toLowerCase().includes('custom')

    // Hide toggle only for non-custom switches
    return !isCustomSwitch
  })
}

/**
 * @function useFeatureToggleStatus
 * Custom Hook that determines if the "FAST Features" toggle should be hidden.
 * This hook depends on the vehicle's subtype and currently active search filters.
 *
 * @param {string} serialNumber - The vehicle serial number to fetch its subtype.
 * @returns {boolean} true if the toggle should be hidden; false otherwise.
 */
export const useFeatureToggleStatus = (serialNumber) => {
  // Redux/State hooks must be executed inside the custom hook
  const filtersObject = useSelector(getFiltersByCollection, FEATURES)
  const { subType } = useSelector(selectVehicleBySerial, { serialNumber })

  // Use useMemo to calculate the value only when dependencies change
  const hideFastFeaturesToggle = useMemo(() => {
    // 1. Exclusion condition by pre-defined subtypes (e.g., specific trucks)
    if (FAST_FEATURES_EXCLUDED_SUBTYPES.includes(subType)) return true

    // 2. Exclusion condition by active filters (checks for the two specific rules)
    const filtersArray = Object.values(filtersObject || {})
    if (filtersArray.some(checkFilterForExclusion)) return true

    // 3. Exclusion condition when SMART_SWITCH exists as a filter key
    if (checkFilterForSmartSwitch(filtersObject)) return true

    return false
  }, [subType, filtersObject])

  return hideFastFeaturesToggle
}

/**
 * @function getCollectionName
 * Maps tab values to collection names
 * @param {string} tabValue - The tab value (ADD_REMOVE_FEATURES, ADD_REMOVE_FEATURE_COLLECTIONS, APPLY_FAST_FEATURES)
 * @returns {string} The collection name (FEATURE, FEATURE_COLLECTIONS, FAST_FEATURES)
 */
export const getCollectionName = (tabValue) => {
  switch (tabValue) {
    case ADD_REMOVE_FEATURES:
      return FEATURE
    case ADD_REMOVE_FEATURE_COLLECTIONS:
      return FEATURE_COLLECTIONS
    case APPLY_FAST_FEATURES:
      return FAST_FEATURES
    default:
      return FEATURE
  }
}

/**
 * @function useCollectionSearchTexts
 * Custom hook to retrieve search text for all collections
 * @returns {Object} Object containing search texts for all collections
 */
export const useCollectionSearchTexts = () => {
  const featureSearchText = useSelector(getSearchStringByCollection, FEATURE)
  const featureCollectionsSearchText = useSelector(
    getSearchStringByCollection,
    FEATURE_COLLECTIONS
  )
  const fastFeaturesSearchText = useSelector(
    getSearchStringByCollection,
    FAST_FEATURES
  )

  return {
    [FEATURE]: featureSearchText,
    [FEATURE_COLLECTIONS]: featureCollectionsSearchText,
    [FAST_FEATURES]: fastFeaturesSearchText,
  }
}

/**
 * @function getCurrentSearchText
 * Gets the current search text based on collection name
 * @param {string} collectionName - The collection name (FEATURE, FEATURE_COLLECTIONS, FAST_FEATURES)
 * @param {Object} searchTexts - Object containing all search texts
 * @returns {string} The current search text for the specified collection
 */
export const getCurrentSearchText = (collectionName, searchTexts) => {
  switch (collectionName) {
    case FEATURE:
      return searchTexts[FEATURE]
    case FEATURE_COLLECTIONS:
      return searchTexts[FEATURE_COLLECTIONS]
    case FAST_FEATURES:
      return searchTexts[FAST_FEATURES]
    default:
      return ''
  }
}

/**
 * @function useHandleTabChange
 * Custom hook that returns a handler function for tab changes with search preservation logic
 * @param {string} serialNumber - The vehicle serial number
 * @param {string} currentValue - The current tab value
 * @returns {Function} Handler function for tab changes
 */
export const useHandleTabChange = (serialNumber, currentValue) => {
  const dispatch = useDispatch()
  const searchTexts = useCollectionSearchTexts()

  const handleTabChange = useCallback(
    (value) => {
      // Reset collections and search first
      dispatch(resetCollections())
      dispatch(resetAllSearch())
      dispatch(setSelectedIdWithDrawer(value))

      // Get current collection name and search state
      const currentCollectionName = getCollectionName(currentValue)
      const newCollectionName = getCollectionName(value)

      // Get current search text based on current collection
      const currentSearchText = getCurrentSearchText(
        currentCollectionName,
        searchTexts
      )

      // Only preserve search text when switching tabs
      if (currentSearchText) {
        // Apply search text to new collection
        dispatch(setSearchString(currentSearchText, newCollectionName))

        // Reset filters for the new collection
        dispatch(setFilters({}, newCollectionName))

        // Trigger search with preserved search text
        setTimeout(() => {
          const addToSearchRequestBody = {
            serial: serialNumber,
            filterConflicts: true,
          }
          dispatch(
            handleSubmitSearch(
              newCollectionName,
              true,
              true,
              addToSearchRequestBody
            )
          )
        }, 100)
      }
      // If no search text, just switch tabs - normal behavior is unchanged
    },
    [dispatch, currentValue, searchTexts, serialNumber]
  )

  return handleTabChange
}
